import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import Button from "./Button";
import profile from "../_data/profile.json";
import { rich, useCopy } from "../i18n/useCopy";
import { yearsOfExperience } from "../seo/meta.mjs";

/**
 * Represents the About Me section.
 * Displays information about the user.
 *
 * @component
 */

const AboutMe = () => {
  // Using react-intersection-observer to determine if the component is in view
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  const years = yearsOfExperience(profile.careerStart);
  const t = useCopy();

  // Variants for staggered animations
  const staggerVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  // Variants for paragraph animations
  const paragraphVariants = {
    initial: { y: 20, opacity: 0 },
    animate: { y: 0, opacity: 1 },
  };

  return (
    <section className="about" id="about" aria-labelledby="about-title" ref={ref}>
      <div className="aboutContainer container">
        <div className="row">
          <motion.div
            className="personalImage col-12 col-lg-5"
            initial={{ x: "-5vw", opacity: 0 }}
            animate={inView ? { x: 0, opacity: 1 } : {}}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          >
            <picture>
              <source
                type="image/webp"
                srcSet="/images/khachik-harutyunyan-480.webp 480w, /images/khachik-harutyunyan.webp 896w"
                sizes="(min-width: 992px) 420px, 80vw"
              />
              <img
                src="/images/khachik-harutyunyan.jpg"
                alt={t.about.imageAlt}
                width="896"
                height="1200"
                loading="lazy"
              />
            </picture>
          </motion.div>
          <div className="personalInfo col-12 col-lg-7">
            <motion.div
              className="contentContainer"
              variants={staggerVariants}
              initial="initial"
              animate={inView ? "animate" : "initial"}
            >
              <motion.p className="sectionEyebrow" variants={paragraphVariants}>
                {t.about.eyebrow}
              </motion.p>
              <motion.h2 id="about-title" className="sectionTitle" variants={paragraphVariants}>
                {t.about.title}
              </motion.h2>

              <div className="contentDescription">
                {t.about.paragraphs.map((p) => (
                  <motion.p key={p.slice(0, 24)} variants={paragraphVariants}>
                    {rich(p, years)}
                  </motion.p>
                ))}
              </div>

              <motion.div className="aboutCtas" variants={paragraphVariants}>
                <Button name={t.about.portfolio} to="/portfolio" />
                <Button name={t.about.resume} to="/resume" variant="ghost" />
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Key numbers */}
        <motion.ul
          className="stats"
          variants={staggerVariants}
          initial="initial"
          animate={inView ? "animate" : "initial"}
        >
          {t.stats.map((stat) => (
            <motion.li key={stat.label} variants={paragraphVariants}>
              <strong>{stat.dynamic === "years" ? `${years}+` : stat.value}</strong>
              <span>{stat.label}</span>
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
};

export default AboutMe;
